import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { CalendarIcon, Phone, MessageCircle, Loader2, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { API, PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL } from "@/constants/site";
import { useAuth } from "@/context/AuthContext";

export default function QuoteSection() {
  const { user, login } = useAuth();
  const [form, setForm] = useState({ nome: "", telefono: "", messaggio: "" });
  const [sending, setSending] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [booking, setBooking] = useState({ nome: "", telefono: "", note: "" });
  const [date, setDate] = useState(null);
  const [calOpen, setCalOpen] = useState(false);
  const [bookingSending, setBookingSending] = useState(false);

  const submitQuote = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.telefono.trim()) {
      toast.error("Inserisci nome e telefono: ti richiamiamo noi.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/quotes`, form, { withCredentials: true });
      toast.success("Richiesta inviata! Ti ricontattiamo al più presto.");
      setForm({ nome: "", telefono: "", messaggio: "" });
    } catch {
      toast.error("Errore nell'invio. Riprova o chiamaci direttamente.");
    } finally {
      setSending(false);
    }
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!booking.nome.trim() || !booking.telefono.trim() || !date) {
      toast.error("Servono nome, telefono e data del sopralluogo.");
      return;
    }
    setBookingSending(true);
    try {
      await axios.post(`${API}/bookings`, { ...booking, data: format(date, "yyyy-MM-dd") }, { withCredentials: true });
      toast.success(`Sopralluogo prenotato per il ${format(date, "d MMMM yyyy", { locale: it })}. Ti confermiamo a breve!`);
      setBookingOpen(false);
      setBooking({ nome: "", telefono: "", note: "" });
      setDate(null);
    } catch {
      toast.error("Errore nella prenotazione. Riprova o chiamaci.");
    } finally {
      setBookingSending(false);
    }
  };

  return (
    <section id="preventivo" data-testid="quote-section" className="bg-slate-900 py-24">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-2">
        <div className="flex flex-col justify-center">
          <p className="text-sm font-bold uppercase tracking-widest text-orange-400">Preventivo gratuito</p>
          <h2 className="mt-3 text-3xl font-semibold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Lasciaci i tuoi dati, ti richiamiamo noi
          </h2>
          <p className="mt-4 max-w-md text-base text-slate-300 md:text-lg">
            Raccontaci cosa devi svuotare: ti rispondiamo in fretta con un prezzo chiaro,
            gratis e senza impegno.
          </p>

          <div className="mt-10 space-y-4">
            <Button
              data-testid="open-booking-dialog-button"
              onClick={() => (user ? setBookingOpen(true) : login())}
              className="w-full rounded-md bg-white font-semibold text-slate-900 transition-transform duration-200 hover:scale-[1.02] hover:bg-slate-100 sm:w-auto"
            >
              <CalendarIcon className="mr-2 h-5 w-5" /> Prenota un sopralluogo gratuito
            </Button>
            <div className="flex flex-col gap-4 sm:flex-row">
              <a
                href={PHONE_TEL}
                data-testid="quote-call-button"
                className="flex items-center gap-3 rounded-md border border-slate-700 px-5 py-3 text-white transition-colors duration-200 hover:border-orange-400"
              >
                <Phone className="h-5 w-5 text-orange-400" />
                <span className="text-sm"><span className="block text-slate-400">Chiama ora</span><span className="font-semibold">{PHONE_DISPLAY}</span></span>
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="quote-whatsapp-button"
                className="flex items-center gap-3 rounded-md border border-green-700 bg-green-600/10 px-5 py-3 text-white transition-colors duration-200 hover:bg-green-600/20"
              >
                <MessageCircle className="h-5 w-5 text-green-400" />
                <span className="text-sm"><span className="block text-slate-400">WhatsApp</span><span className="font-semibold">Risposta rapida</span></span>
              </a>
            </div>
          </div>
        </div>

        {user ? (
        <form
          onSubmit={submitQuote}
          data-testid="quote-form"
          className="rounded-lg border border-slate-700 bg-white p-8 lg:p-10"
        >
          <h3 className="font-display text-2xl font-semibold text-slate-900">Modulo richiesta preventivo</h3>
          <div className="mt-6 space-y-5">
            <div>
              <Label htmlFor="quote-nome" className="text-slate-700">Nome e cognome *</Label>
              <Input
                id="quote-nome"
                data-testid="quote-nome-input"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="Mario Rossi"
                className="mt-2 border-slate-300 focus-visible:ring-orange-500"
                required
              />
            </div>
            <div>
              <Label htmlFor="quote-telefono" className="text-slate-700">Telefono *</Label>
              <Input
                id="quote-telefono"
                data-testid="quote-telefono-input"
                type="tel"
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                placeholder="333 123 4567"
                className="mt-2 border-slate-300 focus-visible:ring-orange-500"
                required
              />
            </div>
            <div>
              <Label htmlFor="quote-messaggio" className="text-slate-700">Cosa dobbiamo sgomberare?</Label>
              <Textarea
                id="quote-messaggio"
                data-testid="quote-messaggio-input"
                value={form.messaggio}
                onChange={(e) => setForm({ ...form, messaggio: e.target.value })}
                placeholder="Es. cantina di circa 20 mq con mobili e scatole, zona centro"
                rows={4}
                className="mt-2 border-slate-300 focus-visible:ring-orange-500"
              />
            </div>
            <Button
              type="submit"
              data-testid="quote-submit-button"
              disabled={sending}
              className="w-full rounded-md bg-orange-500 py-6 text-base font-semibold text-white transition-transform duration-200 hover:scale-[1.01] hover:bg-orange-600"
            >
              {sending ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null}
              Invia richiesta gratuita
            </Button>
            <p className="text-center text-xs text-slate-500">Nessun impegno. Ti ricontattiamo entro il giorno lavorativo.</p>
          </div>
        </form>
        ) : (
        <div
          data-testid="quote-login-gate"
          className="flex flex-col items-center justify-center rounded-lg border border-slate-700 bg-white p-8 text-center lg:p-10"
        >
          <LogIn className="h-10 w-10 text-orange-500" />
          <h3 className="mt-4 font-display text-2xl font-semibold text-slate-900">Accedi per richiedere il preventivo</h3>
          <p className="mt-3 max-w-sm text-sm text-slate-600">
            Per inviare la richiesta ti chiediamo di accedere con Google: ci vogliono 10 secondi
            e così possiamo ricontattarti senza errori.
          </p>
          <Button
            onClick={login}
            data-testid="quote-login-button"
            className="mt-6 rounded-md bg-orange-500 px-8 font-semibold text-white transition-transform duration-200 hover:scale-[1.02] hover:bg-orange-600"
          >
            Accedi con Google
          </Button>
          <p className="mt-4 text-xs text-slate-500">Oppure chiamaci al {PHONE_DISPLAY}: nessun account necessario.</p>
        </div>
        )}
      </div>

      <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
        <DialogContent data-testid="booking-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Prenota un sopralluogo</DialogTitle>
            <DialogDescription>Gratuito e senza impegno: veniamo a vedere di persona e ti facciamo il prezzo esatto.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitBooking} className="space-y-5 pt-2" data-testid="booking-form">
            <div>
              <Label htmlFor="booking-nome">Nome e cognome *</Label>
              <Input
                id="booking-nome"
                data-testid="booking-nome-input"
                value={booking.nome}
                onChange={(e) => setBooking({ ...booking, nome: e.target.value })}
                className="mt-2"
                required
              />
            </div>
            <div>
              <Label htmlFor="booking-telefono">Telefono *</Label>
              <Input
                id="booking-telefono"
                data-testid="booking-telefono-input"
                type="tel"
                value={booking.telefono}
                onChange={(e) => setBooking({ ...booking, telefono: e.target.value })}
                className="mt-2"
                required
              />
            </div>
            <div>
              <Label>Data preferita *</Label>
              <Popover open={calOpen} onOpenChange={setCalOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="booking-date-button"
                    className="mt-2 w-full justify-start text-left font-normal"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {date ? format(date, "EEEE d MMMM yyyy", { locale: it }) : "Scegli una data"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => { setDate(d); setCalOpen(false); }}
                    locale={it}
                    disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label htmlFor="booking-note">Note (indirizzo, tipo di locale...)</Label>
              <Textarea
                id="booking-note"
                data-testid="booking-note-input"
                value={booking.note}
                onChange={(e) => setBooking({ ...booking, note: e.target.value })}
                rows={3}
                className="mt-2"
              />
            </div>
            <Button
              type="submit"
              data-testid="booking-submit-button"
              disabled={bookingSending}
              className="w-full rounded-md bg-orange-500 font-semibold text-white hover:bg-orange-600"
            >
              {bookingSending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Conferma prenotazione
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
